import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { getHotelRoom } from "@/lib/hotelsApi";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const formSchema = z.object({
  room_name: z.string().min(1, "Room name is required"),
  room_description: z.string().optional(),
  price_room_only: z.string().optional(),
  price_breakfast: z.string().optional(),
  price_half_board: z.string().optional(),
  price_full_board: z.string().optional(),
  max_occupancy: z.string().optional(),
  room_image_url: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface RoomViewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roomId?: number;
  hotelId?: number;
  onSuccess: () => void;
}

const RoomViewModal = ({ open, onOpenChange, roomId, hotelId, onSuccess }: RoomViewModalProps) => {
  const { toast } = useToast();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      room_name: "",
      room_description: "",
      price_room_only: "",
      price_breakfast: "",
      price_half_board: "",
      price_full_board: "",
      max_occupancy: "",
      room_image_url: "",
    },
  });


  useEffect(() => {
    if (open && roomId) {
      form.reset({
        room_name: "",
        room_description: "",
        price_room_only: "",
        price_breakfast: "",
        price_half_board: "",
        price_full_board: "",
        max_occupancy: "",
        room_image_url: "",
      });
      loadRoomData();
    } else if (!open) {
      form.reset({
        room_name: "",
        room_description: "",
        price_room_only: "",
        price_breakfast: "",
        price_half_board: "",
        price_full_board: "",
        max_occupancy: "",
        room_image_url: "",
      });
    }
  }, [open, roomId]);

  const loadRoomData = async () => {
    if (!roomId || !hotelId) return;
    
    try {
      const data = await getHotelRoom(hotelId, roomId);
      if (data) {
        form.reset({
          room_name: data.room_name || "",
          room_description: data.room_description || "",
          price_room_only: data.price_room_only?.toString() || "",
          price_breakfast: data.price_breakfast?.toString() || "",
          price_half_board: data.price_half_board?.toString() || "",
          price_full_board: data.price_full_board?.toString() || "",
          max_occupancy: data.max_occupancy?.toString() || "",
          room_image_url: data.room_image_url || "",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>View Room Details</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-4">
            {form.watch("room_image_url") && (
              <div className="rounded-md overflow-hidden">
                <img
                  src={form.watch("room_image_url")}
                  alt="Room preview"
                  className="w-full h-48 object-cover"
                />
              </div>
            )}

            <FormField
              control={form.control}
              name="room_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Room Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Deluxe Suite" {...field} disabled />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="room_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Room description..." {...field} disabled />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="max_occupancy"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Max Occupancy</FormLabel>
                  <FormControl>
                    <Input type="number" placeholder="2" {...field} disabled />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="price_room_only"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price - Room Only</FormLabel>
                    <FormControl>
                      <MoneyInput placeholder="0.00" className="w-[140px]" {...field} disabled />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="price_breakfast"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price - Breakfast</FormLabel>
                    <FormControl>
                      <MoneyInput placeholder="0.00" className="w-[140px]" {...field} disabled />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="price_half_board"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price - Half Board</FormLabel>
                    <FormControl>
                      <MoneyInput placeholder="0.00" className="w-[140px]" {...field} disabled />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="price_full_board"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price - Full Board</FormLabel>
                    <FormControl>
                      <MoneyInput placeholder="0.00" className="w-[140px]" {...field} disabled />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default RoomViewModal;
